/**
 * SÉCURITÉ ROUTIÈRE — Chiffres clés officiels
 * ─────────────────────────────────────────────
 *
 * Source : ONISR (Observatoire National Interministériel de la Sécurité
 * Routière) — Bilan annuel publié chaque année en mars/avril.
 *
 * 🛑 IMPORTANT : Pour mettre à jour ces chiffres :
 *  1. Aller sur https://www.onisr.securite-routiere.gouv.fr/
 *     → Section « Bilans annuels » ou « L'essentiel »
 *  2. Télécharger le dernier PDF « L'essentiel de la sécurité routière »
 *  3. Mettre à jour les valeurs ci-dessous + la date `lastUpdate`
 *  4. Pousser une release : le composant s'actualise automatiquement
 *
 * Pas d'API officielle ONISR pour ces synthèses → mise à jour manuelle
 * volontaire (les données ne bougent qu'une fois par an).
 */

export const SECURITE_ROUTIERE_META = {
  lastUpdate: '2026-08-15',   // ← Baromètre ONISR juillet 2026 intégré
  bilanYear: 2026,             // ← 12 mois glissants (août 2025 → juillet 2026)
  bilanStatus: 'provisoire · baromètre mensuel',
  /**
   * Sources officielles. Chaque entrée peut être désactivée en passant
   * `active: false` — le lien sera masqué automatiquement côté UI par
   * `SecuriteRoutiereStats`. Utile quand une URL officielle devient 404
   * (le site source est parfois restructuré sans notice).
   *
   * URLs mises à jour août 2026 : baromètre mensuel de juillet 2026 publié
   * le 07/08/2026. PDF officiel téléchargeable en local.
   */
  sources: [
    {
      label: 'ONISR — Baromètre juillet 2026 (publié le 07/08/2026)',
      url: 'https://www.onisr.securite-routiere.gouv.fr/',
      active: true,
    },
    {
      label: 'PDF officiel — Baromètre ONISR juillet 2026',
      url: '/documents/barometre-onisr-juillet-2026.pdf',
      active: true,
    },
    {
      label: 'data.gouv.fr — BAAC (Bulletin Analyse Accidents Corporels)',
      url: 'https://www.data.gouv.fr/',
      active: true,
    },
  ],
};

/**
 * Bloc 1 — Chiffres globaux France entière (tous usagers confondus)
 * Source : Baromètre ONISR juillet 2026 — cumul 12 mois glissants
 * (période août 2025 → juillet 2026, estimations arrêtées au 07/08/2026).
 */
export const STATS_GLOBALES = {
  morts: { value: 3260, label: 'Morts sur la route (12 mois glissants)', unit: '', evolution: '+3 % vs 12 mois précédents' },
  blesses: { value: 235000, label: 'Blessés', unit: '', evolution: 'stable' },
  blessesGraves: { value: 16800, label: 'Blessés graves (12 mois glissants)', unit: '', evolution: '+6 % vs 12 mois précédents' },
  accidentsCorporels: { value: 53000, label: 'Accidents corporels', unit: '', evolution: 'stable' },
};

/**
 * Bloc 2 — Focus 2 Roues Motorisées (motos + scooters)
 * Source baromètre ONISR juillet 2026 — cumul 12 mois glissants.
 */
export const STATS_2RM = {
  morts2rm: { value: 682, label: 'Motards et scootéristes tués (12 mois)', subtext: '−3 % vs 12 mois précédents · −9 % vs 2019' },
  partTrafic: { value: 1.9, label: 'Part dans le trafic total', unit: '%' },
  surrisque: { value: 25, label: 'Surrisque de mortalité vs voiture', unit: 'x' },
  morts125: { value: 95, label: 'Tués sur 2RM ≤ 125 cm³', subtext: 'Cyclomoteurs + motos légères' },
  facteurComportemental: { value: 85, label: 'Accidents mortels avec facteur comportemental', unit: '%' },
  blessesGraves: { value: 5300, label: 'Blessés graves 2RM (12 mois)', subtext: '~1 tiers des blessés graves totaux · +3 %' },
};

/**
 * Bloc 3 — Causes principales des accidents mortels (tous usagers)
 */
export const STATS_CAUSES = [
  { cause: 'Vitesse excessive ou inadaptée', pct: 27, color: '#dc2626' },
  { cause: 'Alcool', pct: 23, color: '#7c3aed' },
  { cause: 'Stupéfiants', pct: 13, color: '#ea580c' },
  { cause: 'Inattention / téléphone', pct: 11, color: '#0891b2' },
  { cause: 'Refus de priorité', pct: 9, color: '#16a34a' },
  { cause: 'Fatigue / somnolence', pct: 5, color: '#6b7280' },
];

/**
 * Bloc 4 — Stats par tranche d'âge (utile pour CCS et message pédago élève)
 */
export const STATS_AGE = {
  jeunes1824: { value: 20, label: '% des morts route', subtext: 'pour seulement 8 % de la population' },
  seniors65plus: { value: 27, label: '% des morts route', subtext: 'population vieillissante' },
  enfantsMoins15: { value: 2, label: '% des morts route', subtext: 'progrès continus' },
};

/**
 * Bloc 5 — Faits saillants 2024 pour candidate / candidat CCS à citer au jury
 * Format : phrase courte mémorisable + chiffre clé
 */
export const FAITS_CCS_JURY = [
  "682 motards tués sur 12 mois glissants (août 2025 → juillet 2026) — en baisse de -3 % vs 12 mois précédents.",
  "Les 2RM représentent ~22 % des tués pour seulement 1,9 % du trafic — surrisque ×25.",
  "85 % des accidents mortels 2RM impliquent un facteur comportemental (vitesse, alcool, fatigue, inattention).",
  "En juillet 2026 : +19 % de mortalité sur autoroute sur le trimestre — point noir de l'été.",
  "Blessés graves toutes catégories : +6 % sur 12 mois — moins de morts, mais plus d'accidents graves.",
];

export default {
  meta: SECURITE_ROUTIERE_META,
  globales: STATS_GLOBALES,
  deuxRoues: STATS_2RM,
  causes: STATS_CAUSES,
  age: STATS_AGE,
  faitsJury: FAITS_CCS_JURY,
};
